import React, { useEffect, useMemo, useRef, useState } from "react";
import { Check, ChevronDown, Search, X } from "lucide-react";
import { btnGhost, btnPrimary, labelCls } from "./shared";

export function crmItem(c) {
  const handle = c.twitter_handle || c.instagram_handle || c.handle || c.onlyfans_handle || "";
  return {
    id: c.id,
    name: c.stage_name || c.legal_name || c.email || c.id,
    handle: handle ? `@${String(handle).replace(/^@/, "")}` : "",
    role: c.type || "",
    country: c.country || "",
  };
}

/**
 * Searchable checkbox (multi) or radio (single) dropdown over existing CRM ids.
 * Only ids are stored, so no CRM records are duplicated.
 * value: string[] when multi, string|null when single.
 */
export default function SearchableSelect({ label, icon, items, value, onChange, multi = true, noneLabel = "None", testId }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [pending, setPending] = useState([]);
  const searchRef = useRef(null);

  const selectedIds = multi ? (value || []) : (value ? [value] : []);
  const byId = useMemo(() => new Map(items.map((i) => [i.id, i])), [items]);

  useEffect(() => {
    if (open) {
      setPending(selectedIds);
      setQuery("");
      setTimeout(() => searchRef.current?.focus(), 0);
    }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    const list = q
      ? items.filter((i) => [i.name, i.handle, i.role, i.country].some((f) => (f || "").toLowerCase().includes(q)))
      : items;
    const sel = new Set(pending);
    return [...list.filter((i) => sel.has(i.id)), ...list.filter((i) => !sel.has(i.id))]; // selected pinned first
  }, [items, query, pending]);

  const toggle = (id) => {
    if (!multi) { setPending([id]); return; }
    setPending((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));
  };
  const commit = () => {
    onChange(multi ? pending : (pending[0] || null));
    setOpen(false);
  };
  const removeChip = (id) => onChange(multi ? selectedIds.filter((x) => x !== id) : null);

  const triggerText = selectedIds.length
    ? (multi ? `${selectedIds.length} selected · Change` : `${byId.get(selectedIds[0])?.name || "Selected"} · Change`)
    : (multi ? "None selected · Select" : `${noneLabel} · Select`);

  return (
    <div data-testid={testId}>
      <label className={labelCls}>{icon} {label}</label>
      <button type="button" onClick={() => setOpen(true)} aria-haspopup="dialog" aria-expanded={open}
        className="w-full flex items-center justify-between gap-2 bg-zinc-900 border border-zinc-700 hover:border-purple-500 rounded-lg px-3 py-2 text-sm text-white transition">
        <span className="truncate">{triggerText}</span>
        <ChevronDown size={16} className="text-zinc-400 shrink-0" />
      </button>

      {selectedIds.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mt-2">
          {selectedIds.map((id) => (
            <span key={id} className="inline-flex items-center gap-1 pl-2.5 pr-1 py-1 rounded-full text-xs bg-purple-900/30 border border-purple-500/30 text-purple-200">
              {byId.get(id)?.name || "Unknown (removed from CRM)"}
              <button type="button" aria-label={`Remove ${byId.get(id)?.name || "entry"}`} onClick={() => removeChip(id)} className="p-0.5 rounded-full hover:bg-purple-500/30"><X size={12} /></button>
            </span>
          ))}
        </div>
      )}

      {open && (
        <div className="fixed inset-0 z-[90] flex items-end sm:items-center justify-center bg-black/70 sm:p-4" onMouseDown={() => setOpen(false)}>
          <div role="dialog" aria-label={label} onMouseDown={(e) => e.stopPropagation()}
            className="w-full sm:max-w-md max-h-[85vh] flex flex-col rounded-t-2xl sm:rounded-2xl border border-zinc-800 bg-zinc-950 shadow-2xl">
            <div className="p-3 border-b border-zinc-800">
              <div className="relative">
                <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
                <input ref={searchRef} value={query} onChange={(e) => setQuery(e.target.value)}
                  placeholder="Search name, @handle, role or country"
                  className="w-full bg-zinc-900 border border-zinc-700 focus:border-purple-500 rounded-lg pl-9 pr-3 py-2 text-sm text-white outline-none" />
              </div>
              {multi && (
                <div className="flex gap-3 mt-2 text-xs">
                  <button type="button" className="text-purple-300 hover:text-white"
                    onClick={() => setPending((p) => Array.from(new Set([...p, ...filtered.map((i) => i.id)])))}>Select all filtered</button>
                  <button type="button" className="text-zinc-400 hover:text-white" onClick={() => setPending([])}>Clear selection</button>
                </div>
              )}
            </div>
            <ul className="overflow-y-auto flex-1 py-1" role={multi ? "listbox" : "radiogroup"}>
              {!multi && (
                <li>
                  <button type="button" onClick={() => setPending([])} className="w-full flex items-center gap-3 px-4 py-2.5 text-left hover:bg-zinc-900">
                    <span className={`w-4 h-4 rounded-full border ${pending.length === 0 ? "border-purple-400 bg-purple-500" : "border-zinc-600"}`} />
                    <span className="text-sm text-zinc-300">{noneLabel}</span>
                  </button>
                </li>
              )}
              {filtered.map((i) => {
                const on = pending.includes(i.id);
                return (
                  <li key={i.id}>
                    <button type="button" onClick={() => toggle(i.id)} role={multi ? "option" : "radio"} aria-selected={on} aria-checked={on}
                      className={`w-full flex items-center gap-3 px-4 py-2.5 text-left hover:bg-zinc-900 ${on ? "bg-purple-900/10" : ""}`}>
                      {multi ? (
                        <span className={`w-4 h-4 rounded border flex items-center justify-center shrink-0 ${on ? "border-purple-400 bg-purple-500" : "border-zinc-600"}`}>{on && <Check size={11} />}</span>
                      ) : (
                        <span className={`w-4 h-4 rounded-full border shrink-0 ${on ? "border-purple-400 bg-purple-500" : "border-zinc-600"}`} />
                      )}
                      <span className="min-w-0 flex-1">
                        <span className="block text-sm text-white truncate">{i.name}</span>
                        {i.handle && <span className="block text-xs text-zinc-500 truncate">{i.handle}</span>}
                      </span>
                      {i.role && <span className="text-[10px] px-1.5 py-0.5 rounded bg-cyan-900/30 text-cyan-300 border border-cyan-500/20">{i.role}</span>}
                      {i.country && <span className="text-[10px] px-1.5 py-0.5 rounded bg-zinc-800 text-zinc-300">{i.country}</span>}
                    </button>
                  </li>
                );
              })}
              {filtered.length === 0 && <li className="px-4 py-8 text-center text-sm text-zinc-500">No matches</li>}
            </ul>
            <div className="flex items-center justify-between gap-2 p-3 border-t border-zinc-800">
              <span className="text-xs text-zinc-400">{pending.length} selected</span>
              <div className="flex gap-2">
                <button type="button" className={btnGhost} onClick={() => setOpen(false)}>Cancel</button>
                <button type="button" className={btnPrimary} onClick={commit}>Done</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
